/**
 * TodoRowEditor — shared "edit a todo row" body. Single source of truth
 * for the field grid (description + priority + due_date + due_time +
 * optional status + optional SMS reminder lead) used by:
 *   - `DraftTodosSection` (Add/Edit Segment dialogs) — minimal: no status
 *   - `InlineTodoForm`    (ItineraryView quick-add)  — minimal: no status
 *   - `EditTodoDialog`    (full edit)                — showStatus + showReminder
 *
 * Same controlled-component contract as PaymentRowEditor — caller owns
 * the state, we surface a `patch` to merge.
 *
 * Props:
 *   todo         — { description, priority, due_date, due_time, status?,
 *                    reminder_minutes_before? } object
 *   onChange     — function(patch) — partial merge into caller state
 *   disabled     — read-only mode
 *   showStatus   — render the Status select (default false). When true,
 *                  the priority/date/time row becomes a 4-col grid with
 *                  status, so EditTodoDialog has parity with the inline
 *                  flow without sprouting a fifth row.
 *   showReminder — render the "SMS reminder (min before)" input when
 *                  both due_date and due_time are set (default false)
 *   testIdPrefix — prefix for data-testids on each field
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TodoRowEditor = ({
  todo,
  onChange,
  disabled = false,
  showStatus = false,
  showReminder = false,
  testIdPrefix = "todo-row",
}) => {
  const cols = showStatus ? "grid-cols-4" : "grid-cols-3";
  const reminderActive = showReminder && todo.due_date && todo.due_time;

  return (
    <div className="space-y-2">
      <div>
        <Label className="text-xs">Description *</Label>
        <Input
          placeholder="What needs to happen?"
          value={todo.description || ""}
          onChange={(e) => onChange({ description: e.target.value })}
          disabled={disabled}
          className="h-9"
          data-testid={`${testIdPrefix}-description`}
        />
      </div>

      <div className={`grid ${cols} gap-2`}>
        <div>
          <Label className="text-xs">Priority</Label>
          <Select
            value={todo.priority || "medium"}
            onValueChange={(val) => onChange({ priority: val })}
            disabled={disabled}
          >
            <SelectTrigger className="h-9" data-testid={`${testIdPrefix}-priority`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper" className="z-[100]">
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {showStatus && (
          <div>
            <Label className="text-xs">Status</Label>
            <Select
              value={todo.status || "not_started"}
              onValueChange={(val) => onChange({ status: val })}
              disabled={disabled}
            >
              <SelectTrigger className="h-9" data-testid={`${testIdPrefix}-status`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs">Due date</Label>
          <Input
            type="date"
            value={todo.due_date || ""}
            onChange={(e) => {
              const next = e.target.value;
              // Clearing the date also clears the time — a bare time is meaningless.
              onChange(next ? { due_date: next } : { due_date: "", due_time: "" });
            }}
            disabled={disabled}
            className="h-9"
            data-testid={`${testIdPrefix}-due-date`}
          />
        </div>
        <div>
          <Label className="text-xs">Due time (optional)</Label>
          <Input
            type="time"
            value={todo.due_time || ""}
            onChange={(e) => onChange({ due_time: e.target.value })}
            // Time without a date is meaningless — disable until a date is set.
            disabled={disabled || !todo.due_date}
            className="h-9"
            data-testid={`${testIdPrefix}-due-time`}
          />
        </div>
      </div>

      {reminderActive && (
        <div>
          <Label className="text-xs">SMS reminder (min before)</Label>
          <Input
            type="number"
            min="1"
            max="240"
            placeholder="Use default from Settings"
            value={todo.reminder_minutes_before ?? ""}
            onChange={(e) => onChange({ reminder_minutes_before: e.target.value })}
            disabled={disabled}
            className="h-9"
            data-testid={`${testIdPrefix}-reminder-lead`}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Leave blank to use your default lead time from Settings.
          </p>
        </div>
      )}
    </div>
  );
};

export default TodoRowEditor;
